<template>
  <div class="infocenter">
    <!-- Beautiful Enhanced Toolbar -->
    <v-toolbar class="custom-toolbar mb-4" flat dense elevation="0">
      <div class="toolbar-content">
        <div class="toolbar-left">
          <div class="logo-wrapper">
            <img :src="logo" class="toolbar-icon" alt="Logo" />
          </div>
          <div class="toolbar-title-wrapper">
            <v-toolbar-title class="custom-toolbar-title">
              {{ $t('news.info-center') }}
            </v-toolbar-title>
          </div>
        </div>
        <div class="toolbar-right">
          <v-btn 
            icon 
            variant="text" 
            class="refresh-btn"
            @click="refresh"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
    </v-toolbar>

    <Loader :loading="showLoader" />

    <div v-if="!isMobileView">
      <InfoCenterDesktop v-if="!showLoader" :newsItems="newsItems" :weather="currentWeather"
        :upcomingEvents="upcomingEvents" :userProfile="userOwnProfile" :canEditCreate="canCreateEdit"
        :rootProfile="rootProfile" :itemClicked="openDialog" :editItem="editItem" :deleteItem="deleteItem"
        :isEditModeParent="isEditMode" @update:isEditModeParent="updateEditMode" />
    </div>
    <div v-else>
      <InfoCenterMobile v-if="!showLoader" :newsItems="newsItems" :weather="currentWeather"
        :upcomingEvents="upcomingEvents" :userProfile="userOwnProfile" :canEditCreate="canCreateEdit"
        :rootProfile="rootProfile" :itemClicked="openDialog" :editItem="editItem" :deleteItem="deleteItem"
        :isEditModeParent="isEditMode" @update:isEditModeParent="updateEditMode" />
    </div>

    <!-- Enhanced Dialog Box for Detailed View -->
    <v-dialog v-model="showDialog" max-width="900px">
      <v-card class="elegant-dialog-card" rounded="xl" elevation="12">
        <!-- Enhanced Dialog Toolbar -->
        <v-toolbar flat class="dialog-toolbar" elevation="0">
          <v-chip 
            variant="elevated" 
            class="category-chip" 
            density="comfortable" 
            pill
          >
            <v-icon start size="16">mdi-tag</v-icon>
            {{ selectedItem?.category }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn 
            icon 
            variant="text"
            class="dialog-close-btn"
            @click="showDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="dialog-content">
          <!-- Main Container with Grid Layout -->
          <v-container fluid>
            <!-- Title Section -->
            <v-row class="mb-3">
              <v-col cols="12">
                <header class="dialog-header">
                  <h1 class="dialog-title">{{ selectedItem?.title }}</h1>
                </header>
              </v-col>
            </v-row>

            <!-- Title Image Section -->
            <v-row v-if="selectedItem?.image" class="mb-4 justify-center">
              <v-col cols="12" lg="8" md="10">
                <v-card flat class="title-image-card" rounded="lg" elevation="4">
                  <v-img 
                    :src="selectedItem.image" 
                    class="title-image" 
                    cover
                  />
                </v-card>
              </v-col>
            </v-row>

            <!-- Description Section -->
            <v-row class="mb-4">
              <v-col cols="12">
                <v-card flat class="description-card" rounded="lg" elevation="2">
                  <div v-html="selectedItem?.text" class="item-text"></div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Additional Images Section -->
            <v-row v-if="selectedItem?.additionalImages && selectedItem.additionalImages.length > 0" class="mb-4">
              <v-col cols="12">
                <v-card flat class="additional-images-card" rounded="lg" elevation="2">
                  <v-card-title class="section-title">
                    <v-icon start color="#285096">mdi-image-multiple</v-icon>
                    {{ $t('news.additional-images') }}
                  </v-card-title>
                  <v-row class="images-grid pa-3" dense>
                    <v-col v-for="(image, index) in selectedItem.additionalImages" :key="index" cols="6" sm="4" md="3">
                      <div 
                        class="additional-image-container" 
                        @click="openImageModal(image)"
                        @touchstart="showHoverEffect(index)" 
                        @touchend="hideHoverEffect(index)"
                      >
                        <v-img :src="image" class="additional-image" cover rounded="lg" />
                        <div :class="['additional-image-hover-text', { visible: activeIndex === index }]">
                          <v-icon>mdi-eye</v-icon>
                          <span>{{ $t('news.view-image') }}</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <!-- Files Section -->
            <v-row v-if="selectedItem?.files && selectedItem.files.length > 0" class="mb-3">
              <v-col cols="12">
                <v-card flat class="files-card" rounded="lg" elevation="2">
                  <v-card-title class="section-title">
                    <v-icon start color="#285096">mdi-file-document-multiple</v-icon>
                    {{ $t('news.files') }}
                  </v-card-title>
                  <v-row dense class="pa-3">
                    <v-col v-for="(file, index) in selectedItem.files" :key="index" cols="12" sm="6" md="4">
                      <v-card 
                        class="file-card" 
                        rounded="lg" 
                        elevation="3"
                      >
                        <v-card-item>
                          <template v-slot:prepend>
                            <v-icon color="#285096" size="32">mdi-file-document</v-icon>
                          </template>
                          <v-card-title class="file-name">
                            {{ file.fileName }}
                          </v-card-title>
                        </v-card-item>

                        <v-card-actions class="file-actions">
                          <v-btn 
                            class="file-action-btn"
                            variant="text"
                            color="#285096"
                            @click="viewFile(file)"
                          >
                            <v-icon start>mdi-eye</v-icon>
                            {{ $t('news.view') }}
                          </v-btn>
                          <v-spacer />
                          <v-btn 
                            class="file-action-btn"
                            variant="text"
                            color="#28A745"
                            @click="downloadFile(file)"
                          >
                            <v-icon start>mdi-download</v-icon>
                            {{ $t('news.download') }}
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <!-- PDF Viewer Section -->
            <v-row v-if="pdfDialog" class="mb-4">
              <v-col cols="12">
                <v-card flat class="pdf-viewer-card" rounded="lg" elevation="4">
                  <PDF v-if="pdfSrc" :src="pdfSrc" />
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>

      <!-- Image Modal -->
      <div v-if="imageModalOpen" class="custom-image-modal" @click="closeImageModal">
        <div class="custom-image-modal-content" @click.stop>
          <button class="close-button" @click="closeImageModal">
            <v-icon>mdi-close</v-icon>
          </button>
          <img :src="currentImageSrc" alt="Enlarged Image" class="modal-image" />
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue'
import { getToolbarLogoImage, getCustomer } from '@/assets/Branding/branding'
import user_avatar from '@/assets/General/user_avatar.png'
import Loader from '@/components/general/Loader.vue'
import defaultInfoItem from '@/assets/Branding/Pia/defaultInfoItem.png'
import { getEvents, getNews, createGetImageByContext } from '@/services/HBC/GeminiHelper'
import {
  getInfoCategories,
  getInfoItems,
  getFile,
  getDataFromBlob,
  getUserProfiles,
  getFileInfo,
  isStaingOrQa,
  deleteInfoItem,
  getFeatures,
  getUserProfileById,
} from '@/controllers/BaseController'
import { removeStoredItem } from '@/services/utils'
import { storeBlob } from '@/services/utils'
import { getUsers } from '@/controllers/BackboneAPI'
import moment from 'moment'
import Swal from 'sweetalert2'
import { useRouter } from 'vue-router'
import InfoCenterDesktop from '@/components/general/ChildComponents/InfoCenterDesktop.vue'
import InfoCenterMobile from '@/components/general/ChildComponents/InfoCenterMobile.vue'
import lodash from 'lodash'
import PDF from 'pdf-vue3'
import { useOAuthService } from '@/services/OAuthService'

export default defineComponent({
  components: {
    Loader,
    InfoCenterDesktop,
    InfoCenterMobile,
    PDF,
  },
  setup() {
    const logo = ref('')
    const router = useRouter()
    const OAuthService = useOAuthService()
    let userOwnProfile = ref({})
    const isMobileView = ref(window.innerWidth <= 960)
    const allInfoItems = ref([])
    const isEditMode = ref(false)
    const canCreateEdit = ref(false)
    const currentWeather = ref({})
    const teamMembers = ref([])
    const upcomingEvents = ref([])
    const newsItems = ref([])
    const showLoader = ref(false)
    const rootProfile = ref({})
    const showDialog = ref(false)
    const selectedItem = ref(null)
    const pdfDialog = ref(false)
    const pdfSrc = ref('')
    const infoCategories = ref([])
    const imageModalOpen = ref(false)
    const currentImageSrc = ref('')
    const activeIndex = ref(null)
    const showTeamMembers = ref(false)

    const handleResize = () => {
      isMobileView.value = window.innerWidth <= 960
    }

    const editItem = item => {
      item = JSON.parse(JSON.stringify(item))
      if (!item?.id) {
        let INFOITEMS = JSON.parse(JSON.stringify(allInfoItems.value))
        item = lodash.find(INFOITEMS, { title: item.title })
      }
      router.push({ name: 'EditInfoItem', params: { id: item.id } }).catch(err => console.error(err))
    }

    const deleteItem = async item => {
      try {
        item = JSON.parse(JSON.stringify(item))
        console.log('Deleting item:', item)
        if (!item?.id) {
          let INFOITEMS = JSON.parse(JSON.stringify(allInfoItems.value))
          item = lodash.find(INFOITEMS, { title: item.title })
        }

        const result = await Swal.fire({
          title: 'Are you sure?',
          text: 'Do you really want to delete this item? This action cannot be undone.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, delete it!',
          confirmButtonColor: '#28A745',
          cancelButtonColor: '#DC3545',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
        })

        if (result.isConfirmed) {
          Swal.fire({
            title: 'Deleting...',
            text: 'Please wait while we delete the item.',
            icon: 'info',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
              Swal.showLoading()
            },
          })

          showLoader.value = true
          await deleteInfoItem(item.id)

          Swal.fire({
            title: 'Deleted!',
            text: 'The item has been deleted successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          }).then(async () => {
            let countdown = 3
            const countdownSwal = Swal.fire({
              title: 'Refreshing in 3 seconds...',
              text: 'Refreshing in 3 seconds...',
              icon: 'info',
              timer: 3000,
              allowOutsideClick: false,
              showConfirmButton: false,
              willOpen: () => {
                const interval = setInterval(() => {
                  countdown--
                  Swal.update({
                    text: `Refreshing in ${countdown} seconds...`,
                  })
                  if (countdown === 0) clearInterval(interval)
                }, 1000)
              },
            })

            await countdownSwal
            await removeStoredItem('infoItems')
            await removeStoredItem('infoCategories')
            await removeStoredItem('factoryNews')
            refreshPage()
          })
        }
      } catch (error) {
        console.log(error)
        Swal.fire({
          title: 'Failed!',
          text: 'Failed to delete the item. Please try again.',
          icon: 'error',
          timer: 2000,
          showConfirmButton: false,
        })
      } finally {
        showLoader.value = false
      }
    }

    const refreshPage = () => {
      router.go(0)
    }

    window.addEventListener('resize', handleResize)

    const refresh = async () => {
      try {
        showLoader.value = true
        await removeStoredItem('infoItems_de')
        await removeStoredItem('infoItems_en')
        await removeStoredItem('infoCategories')
        await removeStoredItem('factoryNews')
        await removeStoredItem('factoryEvents')
      } catch (error) {
        console.error(error)
      } finally {
        location.reload()
      }
    }

    onMounted(async () => {
      showLoader.value = true

      try {
        await fetchWeather()

        let [
          CUSTOMER,
          features,
          profiles,
          profile,
          logoImage,
          infoItems,
          infoCategories,
          isPIA
        ] = await Promise.all([
          getCustomer(),
          getFeatures(),
          getUserProfiles(),
          OAuthService.getUserProfile(),
          getToolbarLogoImage(),
          getInfoItems(),
          getInfoCategories(),
          isStaingOrQa()
        ])

        rootProfile.value = profile
        logo.value = logoImage
        canCreateEdit.value = ['globaladmin', 'admin'].some(role =>
          profile?.realm_access?.roles?.includes(role)
        )

        if (features?.communcation?.teamMembers) showTeamMembers.value = true

        userOwnProfile.value = profiles.find(p => p.email === profile.email) || {}

        const apiProfile = await getUserProfileById(userOwnProfile.value.id || '') || {}
        userOwnProfile.value.jobTitle = apiProfile.job || 'Employee'

        const imageId = lodash.get(apiProfile, 'files.entities', []).find(f => f.type === 'profileImage')?.id
        const existingImg = apiProfile.profileImage

        if (imageId && !existingImg) {
          try {
            const file = await getFile(imageId)
            userOwnProfile.value.profileImage = apiProfile.profileImage = file
              ? await getDataFromBlob(file)
              : user_avatar
            await storeBlob(`userProfile_${apiProfile.id}`, apiProfile)
          } catch {
            userOwnProfile.value.profileImage = user_avatar
          }
        } else {
          userOwnProfile.value.profileImage = existingImg || user_avatar
        }

        const filterByCategory = (names) =>
          names.flatMap(name =>
            infoCategories
              .filter(cat => cat?.name?.toLowerCase() === name)
              .flatMap(cat => infoItems.filter(item => item.infoCategory.id === cat.id))
          )

        const newsAnnounceItems = filterByCategory(['news', 'announcement'])
        const eventItems = filterByCategory(['events'])

        await Promise.all(
          newsAnnounceItems.map(async item => {
            try {
              const titleImageId = lodash.get(item, 'files.entities', []).find(f => f.type === 'titleimage')?.id
              const text = item?.text || item?.shortText || ''
              const fallbackText = item?.shortText || item?.text || ''

              item.image = titleImageId
                ? await getDataFromBlob(await getFile(titleImageId))
                : await createGetImageByContext(text) || defaultInfoItem

              item.description = fallbackText
            } catch (err) {
              console.error('Error processing news item:', err)
              item.image = defaultInfoItem
              item.description = item?.shortText || item?.text || ''
            }
          })
        )

        newsItems.value = newsAnnounceItems

        const now = moment()
        upcomingEvents.value = eventItems.map(event => {
          let dateStr = event?.shortText
          let parsedDate = moment(dateStr, ['MMMM D, YYYY', 'YYYY-MM-DD', moment.ISO_8601], true)

          if (isPIA && parsedDate.isValid() && parsedDate.isBefore(now)) {
            parsedDate = now.add(7, 'days')
            dateStr = parsedDate.format('MMMM D, YYYY')
          }

          return {
            title: event.title,
            id: event.id,
            description: event.text?.replace(/<\/?[^>]+(>|$)/g, '') || '',
            date: dateStr,
          }
        })

      } catch (err) {
        console.error('onMounted() error:', err)
      } finally {
        showLoader.value = false
        getUsers()
      }
    })

    async function fetchWeather() {
      const apiKey = '4ae793a3dd93a074ff16d7e97b06ec5b'
      const city = 'Munich,DE'
      const response = await fetch(`https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=${apiKey}&units=metric`)
      const data = await response.json()
      currentWeather.value = {
        temp: data.main.temp.toFixed(1),
        desc: data.weather[0].description,
        humidity: data.main.humidity,
        wind: data.wind.speed,
        rain: data.rain ? data.rain['1h'] : 0
      }
    }

    const openDialog = async item => {
      try {
        showLoader.value
        let itemSelected = JSON.parse(JSON.stringify(item))
        let image = lodash.find(itemSelected.files?.entities, { type: 'titleimage' })
        let files = lodash.filter(itemSelected.files?.entities, { type: 'attachment' })
        let additionalImages = lodash.filter(itemSelected.files?.entities, { type: 'image' })

        if (itemSelected.dummy) {
          itemSelected.image = image?.url || defaultInfoItem
          if (additionalImages?.length > 0) {
            itemSelected.additionalImages = additionalImages.map(img => img.url || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e')
          }
          if (files?.length > 0) {
            itemSelected.files = files.map(file => ({
              id: file.id || Math.random().toString(36).substring(2, 15),
              fileName: `Document_${Math.random().toString(36).substring(2, 7)}.pdf`,
              url: 'https://via.placeholder.com/150'
            }))
          }
        } else {
          if (additionalImages?.length > 0) {
            let imageIds = additionalImages.map(image => image.id)
            let detailedImages = []
            for (let imageId of imageIds) {
              let image = await getDataFromBlob(await getFile(imageId))
              detailedImages.push(image)
            }
            itemSelected.additionalImages = detailedImages
          }

          if (files?.length > 0) {
            let fileIds = files.map(file => file.id)
            let detailedFiles = []
            for (let fileId of fileIds) {
              let file = await getFileInfo(fileId)
              file.fileName = file?.fileName?.split('.')[0]
              file.url = await getFile(file.id)
              file.url = await getDataFromBlob(file.url)
              detailedFiles.push(file)
            }
            itemSelected.files = detailedFiles
          }

          itemSelected.image = image ? await getDataFromBlob(await getFile(image.id)) : defaultInfoItem
        }

        let category = infoCategories.value.find(category => category.id === itemSelected.infoCategory?.id)?.name || 'News'
        itemSelected.category = category

        selectedItem.value = itemSelected
        if (!isEditMode.value) {
          showDialog.value = true
        }
      } catch (error) {
        console.error('Error opening dialog:', error)
        showLoader.value = false
      }
    }

    const viewFile = file => {
      pdfDialog.value = false
      pdfSrc.value = null
      setTimeout(() => {
        pdfSrc.value = file.url
        pdfDialog.value = true
      }, 100)
    }

    const downloadFile = file => {
      file = JSON.parse(JSON.stringify(file))
      const link = document.createElement('a')
      link.href = file.url
      link.download = file.fileName.split('.').shift()
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const openImageModal = image => {
      currentImageSrc.value = image
      imageModalOpen.value = true
    }

    const closeImageModal = () => {
      imageModalOpen.value = false
      currentImageSrc.value = ''
    }

    const showHoverEffect = index => {
      activeIndex.value = index
    }

    const hideHoverEffect = () => {
      activeIndex.value = null
    }

    const updateEditMode = newEditMode => {
      isEditMode.value = newEditMode
    }

    return {
      showLoader,
      logo,
      isMobileView,
      upcomingEvents,
      refresh,
      newsItems,
      rootProfile,
      userOwnProfile,
      showDialog,
      selectedItem,
      pdfDialog,
      router,
      pdfSrc,
      openDialog,
      viewFile,
      downloadFile,
      editItem,
      deleteItem,
      infoCategories,
      openImageModal,
      closeImageModal,
      imageModalOpen,
      currentImageSrc,
      showHoverEffect,
      hideHoverEffect,
      activeIndex,
      isEditMode,
      updateEditMode,
      allInfoItems,
      showTeamMembers,
      canCreateEdit,
      currentWeather,
      fetchWeather,
    }
  },
})
</script>

<style scoped>
/* ===== CONTAINER ===== */
.infocenter {
  padding: 1rem;
  background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 50%, #FFFFFF 100%);
  min-height: var(--app-viewport-height, 100vh);
}

:global(.is-native) .infocenter {
  padding-top: 0;
}

/* ===== CUSTOM TOOLBAR ===== */
.custom-toolbar {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 32px rgba(40, 80, 150, 0.25) !important;
}

.custom-toolbar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 1;
  pointer-events: none;
}

.toolbar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 16px;
  position: relative;
  z-index: 1;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-wrapper {
  background: #FFFFFF;
  padding: 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.toolbar-icon {
  height: 30px;
  width: auto;
}

.toolbar-title-wrapper {
  display: flex;
  align-items: center;
}

.custom-toolbar-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  color: #FFFFFF;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
}

.toolbar-right {
  display: flex;
  align-items: center;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  color: white !important;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: rotate(180deg);
}

/* ===== DIALOG STYLES ===== */
.elegant-dialog-card {
  background: linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%);
  border: 1px solid rgba(40, 80, 150, 0.1);
  overflow: hidden;
}

.dialog-toolbar {
  background: linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%);
  border-bottom: 2px solid rgba(40, 80, 150, 0.1);
  padding: 16px 20px;
}

.category-chip {
  background: linear-gradient(135deg, #285096 0%, #23A5E6 100%) !important;
  color: white !important;
  font-weight: 700;
  padding: 12px 20px;
  box-shadow: 0 4px 12px rgba(40, 80, 150, 0.3);
}

.dialog-close-btn {
  background: rgba(40, 80, 150, 0.1);
  transition: all 0.3s ease;
}

.dialog-close-btn:hover {
  background: rgba(220, 53, 69, 0.1);
  transform: rotate(90deg);
}

.dialog-content {
  padding: 24px;
}

/* ===== HEADER ===== */
.dialog-header {
  text-align: center;
  padding: 16px 0;
}

.dialog-title {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
}

/* ===== IMAGE CARDS ===== */
.title-image-card {
  overflow: hidden;
  border: 2px solid rgba(40, 80, 150, 0.1);
}

.title-image {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.title-image-card:hover .title-image {
  transform: scale(1.05);
}

/* ===== DESCRIPTION CARD ===== */
.description-card {
  background: white;
  padding: 24px;
  border: 1px solid rgba(40, 80, 150, 0.1);
}

.item-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #4E5965;
}

/* ===== SECTION TITLES ===== */
.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #001437;
  padding: 16px 20px;
  border-bottom: 2px solid rgba(40, 80, 150, 0.1);
}

/* ===== ADDITIONAL IMAGES ===== */
.additional-images-card {
  background: white;
  border: 1px solid rgba(40, 80, 150, 0.1);
}

.images-grid {
  gap: 12px;
}

.additional-image-container {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  cursor: pointer;
  aspect-ratio: 1;
}

.additional-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
}

.additional-image-container:hover .additional-image {
  transform: scale(1.1);
  opacity: 0.7;
}

.additional-image-hover-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(40, 80, 150, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.additional-image-container:hover .additional-image-hover-text,
.additional-image-hover-text.visible {
  opacity: 1;
}

/* ===== FILES SECTION ===== */
.files-card {
  background: white;
  border: 1px solid rgba(40, 80, 150, 0.1);
}

.file-card {
  background: white;
  border: 1px solid rgba(40, 80, 150, 0.1);
  transition: all 0.3s ease;
}

.file-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(40, 80, 150, 0.15) !important;
}

.file-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #001437;
  word-break: break-word;
}

.file-actions {
  background: #F8FAFC;
  padding: 12px;
}

.file-action-btn {
  font-weight: 600;
  text-transform: none;
}

/* ===== PDF VIEWER ===== */
.pdf-viewer-card {
  background: white;
  padding: 20px;
  border: 2px solid rgba(40, 80, 150, 0.1);
}

/* ===== IMAGE MODAL ===== */
.custom-image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
}

.custom-image-modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  animation: modalZoom 0.3s ease;
}

@keyframes modalZoom {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.modal-image {
  width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.close-button {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 2px solid white;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 24px;
}

.close-button:hover {
  background: rgba(220, 53, 69, 0.8);
  transform: rotate(90deg);
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 960px) {
  .custom-toolbar-title {
    font-size: 1.25rem;
  }

  .toolbar-icon {
    height: 28px;
  }

  .dialog-title {
    font-size: 2rem;
  }
}

@media (max-width: 640px) {
  .infocenter {
    padding: 0.5rem;
  }

  :global(.is-native) .infocenter {
    padding-top: 0;
  }

  .toolbar-content {
    padding: 6px 12px;
  }

  .toolbar-left {
    gap: 10px;
  }

  .logo-wrapper {
    padding: 6px;
  }

  .toolbar-icon {
    height: 26px;
  }

  .custom-toolbar-title {
    font-size: 1.125rem;
  }

  .dialog-content {
    padding: 16px;
  }

  .dialog-title {
    font-size: 1.75rem;
  }

  .item-text {
    font-size: 0.9375rem;
  }

  .section-title {
    font-size: 1.125rem;
    padding: 12px 16px;
  }
}

@media (max-width: 400px) {
  .custom-toolbar-title {
    font-size: 1rem;
  }

  .dialog-title {
    font-size: 1.5rem;
  }

  .item-text {
    font-size: 0.875rem;
  }
}
</style>
